import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, IconButton, Divider,
  CircularProgress,
} from '@mui/material';
import { Close, AccountBalance, AttachMoney, TrendingUp, TrendingDown } from '@mui/icons-material';

export interface ShiftCashDialogProps {
  open: boolean;
  /** 'open' = entering opening cash, 'close' = entering closing cash */
  mode: 'open' | 'close';
  /** Summary stats from the closing shift (only for 'close' mode) */
  summary?: {
    cash_opening?: number;
    total_cash?: number;
    refunds_amount?: number;
    cash_expected?: number;
    orders_count?: number;
    total_sales?: number;
  };
  loading?: boolean;
  onConfirm: (cashAmount: number) => void;
  onSkip?: () => void;
  onClose: () => void;
}

/**
 * FRAUD PREVENTION #7: Cash reconciliation dialog
 * - On shift open: ask for opening float (e.g., €100 in drawer)
 * - On shift close: count drawer, enter actual cash → calculate variance
 * - Variance > €5 = flagged in audit log + admin shift report
 */
const ShiftCashDialog: React.FC<ShiftCashDialogProps> = ({ open, mode, summary, loading, onConfirm, onSkip, onClose }) => {
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (open) setAmount('');
  }, [open]);

  const numericAmount = parseFloat(amount.replace(',', '.')) || 0;
  const expected = summary?.cash_expected || 0;
  const variance = mode === 'close' ? numericAmount - expected : 0;
  const varianceColor = Math.abs(variance) <= 5 ? '#4CAF50' : variance > 0 ? '#FF9800' : '#ef5350';
  const isLargeVariance = Math.abs(variance) > 5;

  const handleConfirm = () => {
    if (amount === '') return;
    onConfirm(numericAmount);
  };

  const isOpenMode = mode === 'open';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: '#1a1d2e', border: '2px solid #00C896' } }}>
      <DialogTitle sx={{ borderBottom: '1px solid #2a2d42', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AccountBalance sx={{ color: '#00C896', fontSize: 28 }} />
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
              {isOpenMode ? 'Open Shift — Cash Drawer' : 'Close Shift — Cash Count'}
            </Typography>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem' }}>
              {isOpenMode ? 'Enter the cash currently in the drawer' : 'Count the drawer and enter the amount'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#9ca3af' }}><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Closing summary (close mode only) */}
        {!isOpenMode && summary && (
          <Box sx={{ mb: 2.5, p: 2, bgcolor: '#13151f', borderRadius: 2, border: '1px solid #2a2d42' }}>
            <Typography sx={{ color: '#00C896', fontSize: '0.7rem', fontWeight: 700, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Shift Summary
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>Orders processed</Typography>
              <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>{summary.orders_count || 0}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>Total sales</Typography>
              <Typography sx={{ color: '#00C896', fontSize: '0.85rem', fontWeight: 700 }}>€{(summary.total_sales || 0).toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ borderColor: '#2a2d42', my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>Opening cash</Typography>
              <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>€{(summary.cash_opening || 0).toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>+ Cash sales</Typography>
              <Typography sx={{ color: '#4CAF50', fontSize: '0.85rem' }}>€{(summary.total_cash || 0).toFixed(2)}</Typography>
            </Box>
            {(summary.refunds_amount || 0) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>− Refunds</Typography>
                <Typography sx={{ color: '#FF9800', fontSize: '0.85rem' }}>−€{(summary.refunds_amount || 0).toFixed(2)}</Typography>
              </Box>
            )}
            <Divider sx={{ borderColor: '#2a2d42', my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ color: '#FFC107', fontSize: '0.85rem', fontWeight: 700 }}>Expected in drawer</Typography>
              <Typography sx={{ color: '#FFC107', fontSize: '1.1rem', fontWeight: 800 }}>€{expected.toFixed(2)}</Typography>
            </Box>
          </Box>
        )}

        {/* Cash input */}
        <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem', mb: 0.5 }}>
          {isOpenMode ? 'Opening cash float (€)' : 'Actual cash counted (€)'}
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <AttachMoney sx={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: '#00C896', fontSize: 28, pointerEvents: 'none',
          }} />
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9.,]/g, '');
              setAmount(val);
            }}
            placeholder="0.00"
            autoFocus
            style={{
              width: '100%', padding: '14px 14px 14px 44px', borderRadius: 12,
              border: `2px solid ${amount ? '#00C896' : '#3a3d52'}`,
              background: '#1e2130', color: '#fff',
              fontSize: '1.6rem', fontWeight: 700, outline: 'none',
              boxSizing: 'border-box', textAlign: 'right',
              fontFamily: 'monospace',
            }}
            onKeyDown={(e) => { if (e.key === 'Enter' && amount) handleConfirm(); }}
          />
        </Box>

        {/* Variance display (close mode) */}
        {!isOpenMode && amount && (
          <Box sx={{
            mt: 2, p: 1.5, borderRadius: 2,
            bgcolor: isLargeVariance ? `${varianceColor}11` : 'rgba(76,175,80,0.08)',
            border: `1px solid ${varianceColor}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {variance > 0 ? <TrendingUp sx={{ color: varianceColor, fontSize: 22 }} />
                : variance < 0 ? <TrendingDown sx={{ color: varianceColor, fontSize: 22 }} />
                : <AccountBalance sx={{ color: varianceColor, fontSize: 22 }} />}
              <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem', fontWeight: 600 }}>
                {variance === 0 ? 'Perfect match!' : variance > 0 ? 'Surplus' : 'Shortage'}
              </Typography>
            </Box>
            <Typography sx={{ color: varianceColor, fontSize: '1.2rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
              {variance > 0 ? '+' : ''}€{variance.toFixed(2)}
            </Typography>
          </Box>
        )}

        {isLargeVariance && amount && (
          <Typography sx={{ color: '#FF9800', fontSize: '0.78rem', mt: 1.5, fontStyle: 'italic' }}>
            ⚠️ Variance over €5 — this will be flagged for admin review.
          </Typography>
        )}

        {isOpenMode && (
          <Typography sx={{ color: '#666', fontSize: '0.75rem', mt: 2, fontStyle: 'italic' }}>
            💡 Tip: Count the cash drawer before starting. If empty or no float used, enter 0.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #2a2d42', px: 3, py: 2, gap: 1 }}>
        {onSkip && isOpenMode && (
          <Button onClick={onSkip} sx={{ color: '#9ca3af', textTransform: 'none' }}>
            Skip (use €0)
          </Button>
        )}
        <Button onClick={onClose} sx={{ color: '#9ca3af', textTransform: 'none' }}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={amount === '' || loading}
          variant="contained"
          sx={{
            background: '#00C896', color: '#1a1d2e', fontWeight: 700, textTransform: 'none',
            '&:hover': { background: '#7bc012' },
            '&.Mui-disabled': { background: '#444', color: '#666' },
          }}>
          {loading ? <CircularProgress size={18} sx={{ color: '#1a1d2e' }} /> : (isOpenMode ? 'Open Shift' : 'Close Shift')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShiftCashDialog;
